import { getEntitiesByFpIdAnnotationsQueryKey, patchEntitiesByFpIdAnnotationsByAnnotationIdMutation } from '@onefootprint/fetch-dashboard';
import { useIntl } from '@onefootprint/hooks';
import { IcoQuoteLeft16 } from '@onefootprint/icons';
import type { Annotation } from '@onefootprint/request-types/dashboard';
import { LinkButton, Tag } from '@onefootprint/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import PermissionGate from '@/components/permission-gate';
import TruncatedText from '@/entity/components/truncated-text';
import useEntityId from '@/entity/hooks/use-entity-id';
export type PinnedNoteProps = {
  annotation: Annotation;
};
const PinnedNote = ({
  annotation
}: PinnedNoteProps) => {
  const {
    t
  } = useTranslation('entity-details');
  const entityId = useEntityId();
  const queryClient = useQueryClient();
  const updateMutation = useMutation(patchEntitiesByFpIdAnnotationsByAnnotationIdMutation({}));
  const {
    formatDateWithTime
  } = useIntl();
  const formatName = (firstName?: string | null, lastName?: string | null, email?: string) => {
    const name = [firstName, lastName].filter(Boolean).join(' ').trim();
    if (name && email) return `${name} (${email})`;
    return name || email || '';
  };
  const renderAuthor = () => {
    if (annotation.source.kind === 'organization') {
      return formatName(annotation.source.firstName, annotation.source.lastName, annotation.source.email);
    }
    if (annotation.source.kind === 'firm_employee') {
      const display = formatName(annotation.source.firstName, annotation.source.lastName, annotation.source.email);
      if (!display) {
        return t($ => $.pinnedNotes.noteAddedBySource.firmEmployee);
      }
      return <span className="flex items-center gap-2">
          <span>{display}</span>
          <Tag>{t($_0 => $_0.actor.footprint)}</Tag>
        </span>;
    }
    if (annotation.source.kind === 'footprint') {
      return t($_1 => $_1.pinnedNotes.noteAddedBySource.footprint);
    }
    if (annotation.source.kind === 'api_key') {
      return t($_2 => $_2.pinnedNotes.noteAddedBySource.apiKey);
    }
    return t($_3 => $_3.pinnedNotes.noteAddedBySource.unknown);
  };
  const handleUnpinNote = async () => {
    const applyOptimisticUpdate = () => {
      const currentAnnotations = queryClient.getQueryData<Annotation[]>(getEntitiesByFpIdAnnotationsQueryKey({
        path: {
          fpId: entityId
        }
      }));
      if (!currentAnnotations) {
        return;
      }
      queryClient.setQueryData(getEntitiesByFpIdAnnotationsQueryKey({
        path: {
          fpId: entityId
        }
      }), currentAnnotations.map(annotationItem => annotationItem.id === annotation.id ? {
        ...annotationItem,
        isPinned: false
      } : annotationItem));
    };
    const revalidateQueries = () => {
      queryClient.invalidateQueries();
    };
    try {
      applyOptimisticUpdate();
      await updateMutation.mutateAsync({
        path: {
          fpId: entityId,
          annotationId: annotation.id
        },
        body: {
          isPinned: false
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      revalidateQueries();
    }
  };
  return annotation.note.length > 0 ? <div className="flex flex-col p-4 gap-3">
      <header className="flex flex-col items-start gap-1 md:flex-row md:items-center md:justify-between md:gap-0">
        <div className="flex flex-wrap items-center md:flex-nowrap">
          <div className="text-label-2 text-primary wrap-anywhere md:wrap-normal">{renderAuthor()}</div>
          <div className="text-label-2 text-primary ml-1">&middot;</div>
          <PermissionGate scopeKind="manual_review" fallbackText={t($_4 => $_4.manualReviewNotAllowed)}>
            <LinkButton className="ml-1" onClick={handleUnpinNote}>
              {t($_5 => $_5.pinnedNotes.unpinButtonText)}
            </LinkButton>
          </PermissionGate>
        </div>
        <div className="text-body-2 text-secondary">{formatDateWithTime(new Date(annotation.timestamp))}</div>
      </header>
      <div className="flex">
        <span className="w-fit">
          <IcoQuoteLeft16 />
        </span>
        <TruncatedText text={annotation.note} truncatedTextNumLines={2} className="ml-3 min-w-0 text-secondary text-body-2 wrap-anywhere md:min-w-auto md:wrap-normal" supportMarkdown />
      </div>
    </div> : null;
};
export default PinnedNote;